import React from 'react';
import { PropTypes } from 'prop-types';
import { styled, alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  Divider,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Collapse,
} from '@mui/material';
import Icon from '@mdi/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { darkTheme, Drawer } from 'daaas-ui';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';

const Root = styled('div')(({ theme }) => ({
  '& .app-bar__menuItem.Mui-selected': {
    padding: theme.spacing(1, 2, 1, 1.5),
    borderLeft: `${theme.spacing(0.5)} solid`,
    borderLeftColor: darkTheme.palette.background.paper,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

export default function MobileMenuDrawer({
  open = false,
  toggleDialog = null,
  openDrawer = null,
  closeDrawer = null,
}) {
  const location = useLocation();
  const path = location.pathname;
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [collapse, setCollapse] = React.useState(true);

  const handleCollapse = () => {
    setCollapse(!collapse);
  };

  return (
    <Drawer
      id="mobile-menu-drawer"
      open={open}
      title={t('menu')}
      footer={false}
      anchor="left"
      toggleDrawer={() => closeDrawer('menuDrawer')}
      variant="persistent"
      closeText={t('common:close')}
    >
      <Root>
        <nav>
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  closeDrawer('menuDrawer');
                  navigate(`${t('common:/urlPrefix')}${t('common:/overview')}`);
                }}
                selected={
                  path === '/data-analytics-services/overview' ||
                  path === '/services-analyse-donnees/apercu'
                }
                className="app-bar__menuItem"
                aria-current={
                  path === '/data-analytics-services/overview' ||
                  path === '/services-analyse-donnees/apercu'
                    ? 'page'
                    : false
                }
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" component="span">
                      {t('overview')}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  closeDrawer('menuDrawer');
                  navigate(`${t('common:/urlPrefix')}${t('common:/aboutdas')}`);
                }}
                selected={
                  path === '/data-analytics-services/aboutdas' ||
                  path === '/services-analyse-donnees/apropossad'
                }
                className="app-bar__menuItem"
                aria-current={
                  path === '/data-analytics-services/aboutdas' ||
                  path === '/services-analyse-donnees/apropossad'
                    ? 'page'
                    : false
                }
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" component="span">
                      {t('about')}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <li>
              <ListItemButton
                onClick={handleCollapse}
                className="app-bar__menuItem"
                aria-expanded={collapse}
                aria-controls="envCollapse"
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" component="span">
                      {t('environments')}
                    </Typography>
                  }
                />
                {collapse ? (
                  <Icon path={mdiChevronUp} size={1} />
                ) : (
                  <Icon path={mdiChevronDown} size={1} />
                )}
              </ListItemButton>
              <Collapse id="envCollapse" in={collapse} timeout="auto">
                <List
                  disablePadding
                  sx={{ pl: 4 }}
                  aria-label={t('environments')}
                >
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        closeDrawer('menuDrawer');
                        navigate(
                          `${t('common:/urlPrefix')}${t('common:/aaw')}`
                        );
                      }}
                      selected={
                        path === '/data-analytics-services/aaw' ||
                        path === '/services-analyse-donnees/etaa'
                      }
                      className="app-bar__menuItem"
                      aria-current={
                        path === '/data-analytics-services/aaw' ||
                        path === '/services-analyse-donnees/etaa'
                          ? 'page'
                          : false
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" component="span">
                            {t('aaw')}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        closeDrawer('menuDrawer');
                        navigate(
                          `${t('common:/urlPrefix')}${t('common:/cae')}`
                        );
                      }}
                      selected={
                        path === '/data-analytics-services/cae' ||
                        path === '/services-analyse-donnees/eac'
                      }
                      className="app-bar__menuItem"
                      aria-current={
                        path === '/data-analytics-services/cae' ||
                        path === '/services-analyse-donnees/eac'
                          ? 'page'
                          : false
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" component="span">
                            {t('cae')}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        closeDrawer('menuDrawer');
                        navigate(
                          `${t('common:/urlPrefix')}${t('common:/gae')}`
                        );
                      }}
                      selected={
                        path === '/data-analytics-services/gae' ||
                        path === '/services-analyse-donnees/eag'
                      }
                      className="app-bar__menuItem"
                      aria-current={
                        path === '/data-analytics-services/gae' ||
                        path === '/services-analyse-donnees/eag'
                          ? 'page'
                          : false
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" component="span">
                            {t('gae')}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Collapse>
            </li>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  closeDrawer('menuDrawer');
                  navigate(`${t('common:/urlPrefix')}${t('common:/pricing')}`);
                }}
                selected={
                  path === '/data-analytics-services/pricing' ||
                  path === '/services-analyse-donnees/cout'
                }
                className="app-bar__menuItem"
                aria-current={
                  path === '/data-analytics-services/pricing' ||
                  path === '/services-analyse-donnees/cout'
                    ? 'page'
                    : false
                }
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" component="span">
                      {t('pricing:pricing')}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          </List>
        </nav>

        <Divider sx={{ mt: 2, mb: 2 }} />
        <nav>
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  openDrawer('helpDrawer');
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" component="span">
                      {t('help')}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  toggleDialog('feedbackDialog', true);
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" component="span">
                      {t('feedback')}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          </List>
        </nav>
      </Root>
    </Drawer>
  );
}

MobileMenuDrawer.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func,
  openDrawer: PropTypes.func,
  closeDrawer: PropTypes.func,
};
